"use client";

import {useEffect} from "react";
import {getBotReason, isConfirmedBot} from "../lib/is-bot";
import {
	initMixpanelClient,
	type InitMixpanelOptions,
} from "../lib/mixpanel-client";
import {registerUtmSuperProperties} from "../../utm/lib/utm-attribution-client";

/**
 * Initialize Mixpanel early in the app lifecycle.
 * This ensures autocapture (pageviews, form submissions) works correctly.
 * Add this to your root layout.
 *
 * Bot detection runs client-side against `navigator.userAgent` rather than a
 * server-read `headers()` call — the header and `navigator.userAgent` carry
 * the same client-supplied value, so reading it server-side bought no extra
 * authority, only cost every page mounting this in its root layout static
 * rendering (a server component awaiting `headers()` forces the whole route
 * dynamic).
 *
 * `known_bot`/`automation_tool` still gate init; `missing_user_agent` is
 * shadow mode — tagged, not gated. See isConfirmedBot's doc comment.
 */
export default function MixpanelInit({
	eventPrefix,
	recordSessionsPercent,
	identifyWithGaClientId,
	autocapturePageview,
}: InitMixpanelOptions = {}) {
	useEffect(() => {
		const botReason = getBotReason(navigator.userAgent);
		if (botReason && !isConfirmedBot(botReason)) {
			console.warn("[mixpanel] tagging suspected bot traffic (shadow mode)", {
				reason: botReason,
				userAgent: navigator.userAgent,
			});
		}
		void initMixpanelClient({
			eventPrefix,
			recordSessionsPercent,
			botReason,
			identifyWithGaClientId,
			autocapturePageview,
		}).then(registerUtmSuperProperties);
		// pass in destructured values to avoid unnecessary re-renders, using
		// objects causes the effect to re-run on every render
	}, [
		eventPrefix,
		recordSessionsPercent,
		identifyWithGaClientId,
		autocapturePageview,
	]);

	return null;
}
